import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Tenant } from './TenantTable';

interface SearchResult extends Tenant {
  // Additional search-specific fields can go here if needed
}

interface TenantSearchBarProps {
  onSearch: (query: string) => void;
  onSelect?: (tenant: SearchResult) => void;
  placeholder?: string;
}

const TenantSearchBar: React.FC<TenantSearchBarProps> = ({
  onSearch,
  onSelect,
  placeholder = "Search by name, email, or tenant..."
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();
  const searchDebounceRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const performSearch = async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `/api/admin/tenants?search=${encodeURIComponent(searchQuery)}&limit=10`
      );
      if (response.ok) {
        const data = await response.json();
        setResults(data.tenants || []);
        setShowDropdown(true);
        setSelectedIndex(-1);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);

    // Debounce the main search (table filtering)
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      onSearch(newQuery);
    }, 300);

    // Debounce autocomplete dropdown search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      performSearch(newQuery);
    }, 200);
  };

  const handleSelectTenant = useCallback((tenant: SearchResult) => {
    setShowDropdown(false);
    setSelectedIndex(-1);
    if (onSelect) {
      onSelect(tenant);
    }
  }, [onSelect]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleSelectTenant(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setShowDropdown(false);
    onSearch('');
    inputRef.current?.focus();
  };

  const highlightMatch = (text: string, searchQuery: string): React.ReactNode => {
    if (!searchQuery.trim()) return text;
    const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <span key={i} className="bg-indigo-500/30 text-indigo-200 font-medium">{part}</span>
      ) : (
        part
      )
    );
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active':
        return 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30';
      case 'trial':
        return 'bg-blue-500/20 text-blue-300 border border-blue-500/30';
      case 'past_due':
        return 'bg-red-500/20 text-red-300 border border-red-500/30';
      case 'cancelled':
        return 'bg-slate-500/20 text-slate-300 border border-slate-500/30';
      default:
        return 'bg-slate-500/20 text-slate-300 border border-slate-500/30';
    }
  };

  return (
    <div ref={searchRef} className="relative w-full max-w-xl">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => query.trim() && results.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className="block w-full pl-10 pr-10 py-2.5 border border-slate-600/50 rounded-xl leading-5 bg-slate-800/50 backdrop-blur-sm text-white placeholder-slate-400 focus:outline-none focus:placeholder-slate-500 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 sm:text-sm transition-all duration-200"
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center space-x-2">
          {isLoading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500"></div>
          )}
          {query && !isLoading && (
            <button
              onClick={handleClear}
              className="text-slate-400 hover:text-white transition-colors"
              title="Clear search"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {showDropdown && results.length > 0 && (
        <div className="absolute z-20 mt-2 w-full bg-slate-800/98 backdrop-blur-md shadow-2xl max-h-80 rounded-xl py-2 text-base border border-slate-700/50 overflow-auto focus:outline-none">
          <div className="px-3 py-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider border-b border-slate-700/50 mb-1">
            {results.length} result{results.length !== 1 ? 's' : ''} found
          </div>
          {results.map((tenant, index) => {
            const displayName = tenant.full_name ||
              `${tenant.first_name || ''} ${tenant.last_name || ''}`.trim() ||
              'Unnamed User';
            const isSelected = index === selectedIndex;

            return (
              <div
                key={tenant.id}
                onClick={() => handleSelectTenant(tenant)}
                className={`cursor-pointer select-none py-3 px-3 mx-1 rounded-lg transition-all duration-150 ${
                  isSelected
                    ? 'bg-indigo-500/20 border border-indigo-500/30'
                    : 'hover:bg-slate-700/50 border border-transparent'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white truncate">
                      {highlightMatch(displayName, query)}
                    </div>
                    <div className="text-sm text-slate-400 truncate">
                      {highlightMatch(tenant.email, query)}
                    </div>
                    {tenant.tenant_name && tenant.tenant_name !== 'No Tenant' && (
                      <div className="text-xs text-slate-500 truncate mt-0.5">
                        Tenant: {highlightMatch(tenant.tenant_name, query)}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      getStatusColor(tenant.subscription_status)
                    }`}>
                      {tenant.subscription_status}
                    </span>
                    {tenant.subscription_tier && (
                      <span className="text-xs text-slate-500">
                        {tenant.subscription_tier}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <div className="px-3 py-2 text-xs text-slate-500 border-t border-slate-700/50 mt-1">
            <kbd className="px-1.5 py-0.5 bg-slate-700 rounded text-slate-400">↑↓</kbd> to navigate,
            <kbd className="px-1.5 py-0.5 bg-slate-700 rounded text-slate-400 ml-1">Enter</kbd> to select,
            <kbd className="px-1.5 py-0.5 bg-slate-700 rounded text-slate-400 ml-1">Esc</kbd> to close
          </div>
        </div>
      )}

      {showDropdown && results.length === 0 && query.trim() && !isLoading && (
        <div className="absolute z-20 mt-2 w-full bg-slate-800/98 backdrop-blur-md shadow-2xl rounded-xl py-4 text-base border border-slate-700/50">
          <div className="text-center">
            <MagnifyingGlassIcon className="h-8 w-8 text-slate-500 mx-auto mb-2" />
            <p className="text-slate-400">No tenants found for "{query}"</p>
            <p className="text-xs text-slate-500 mt-1">
              Try searching by first name, last name, full name, or email
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantSearchBar;